import { useState, createContext, useContext } from 'react'
import { ModifierGroup } from 'components/Locations/Dashboard/types'

type CachedModifierGroups = {
  modifierGroups: ModifierGroup[]
  isStale: boolean
}

type ModifierGroupsState = {
  cachedModifierGroups: CachedModifierGroups | null
  setModifierGroups: (modifierGroups: ModifierGroup[]) => void
  markModifierGroupsStale: () => void
}

export const SetupModifierGroups = (): ModifierGroupsState => {
  const [cachedModifierGroups, setCachedModifierGroups] =
    useState<CachedModifierGroups | null>(null)

  const setModifierGroups = (modifierGroups: ModifierGroup[]) =>
    setCachedModifierGroups({ modifierGroups, isStale: false })

  const markModifierGroupsStale = () =>
    setCachedModifierGroups((prev) =>
      prev ? { ...prev, isStale: true } : prev
    )

  return {
    cachedModifierGroups,
    setModifierGroups,
    markModifierGroupsStale
  }
}

const initialState: ModifierGroupsState = {
  cachedModifierGroups: null,
  setModifierGroups: () => null,
  markModifierGroupsStale: () => null
}

export const ModifierGroupsContext =
  createContext<ModifierGroupsState>(initialState)

// Caches the merchant's modifier groups so every consumer doesn't refetch them
// on mount.
//
// Invalidation marks the cache stale rather than clearing it, so consumers keep
// rendering the previous groups while the refetch is in flight instead of
// flashing an empty list. invalidate() works from anywhere under GlobalContext,
// including screens that MerchantModifierGroupsLoader doesn't wrap (the
// modifier group form lives under ProductsAndModifiers, which has no loader) -
// the next loader to mount sees needsFetch and refreshes.
export const useModifierGroups = () => {
  const { cachedModifierGroups, setModifierGroups, markModifierGroupsStale } =
    useContext(ModifierGroupsContext)

  return {
    modifierGroups: cachedModifierGroups?.modifierGroups || [],
    // Whether there are groups to render at all - drives the loader's gate.
    hasModifierGroups: !!cachedModifierGroups,
    // Whether those groups are missing or out of date - drives the fetch.
    needsFetch: !cachedModifierGroups || cachedModifierGroups.isStale,
    setModifierGroups,
    invalidate: markModifierGroupsStale
  }
}
